/**
 * @file Whether the signed-in user is StriveLabs staff, decided by the domain
 * of their primary email address.
 *
 * Gates internal-only surfaces that are not customer-ready. Domain rather than
 * the `STRIVELABS_USERS` allowlist that `InternalRoute` uses, because the
 * allowlist is a fixed set of addresses and leaves any member of staff missing
 * from it locked out of the surface.
 */

import { useUser } from '@clerk/clerk-react';

/** The domain a StriveLabs address ends in. */
const STRIVELABS_DOMAIN = '@strivelabs.ai';

export interface StrivelabsUserState {
  /** Whether the signed-in user's primary email is a StriveLabs address. */
  isStrivelabsUser: boolean;
  /** Whether Clerk has loaded the user, and so whether the answer has settled. */
  isLoaded: boolean;
}

/**
 * Returns whether the signed-in user's primary email sits on the StriveLabs
 * domain.
 *
 * @returns The staff membership state.
 */
export function useIsStrivelabsUser (): StrivelabsUserState {
  const { user, isLoaded } = useUser();

  const email = user?.primaryEmailAddress?.emailAddress?.toLowerCase();

  return {
    isStrivelabsUser: Boolean(email?.endsWith(STRIVELABS_DOMAIN)),
    isLoaded
  };
}
